/**
 * Check digits for the company identifiers Peppol is sent (BBSW-11615).
 *
 * The back sends them under their GS1/Peppol scheme in
 * `DigitealManagerService::getPeppolIdentifiers()`: a GLN under `0088`, a
 * Belgian enterprise number under `0208`. Users have been pasting that scheme
 * in front of the number itself, which the API stores as-is and Peppol then
 * rejects, so `hasSchemePrefix()` singles that mistake out.
 */

/** Peppol scheme the back prefixes a GLN with when it sends an invoice. */
export const PEPPOL_SCHEME_GLN = '0088'

/** Peppol scheme the back prefixes a Belgian enterprise number with. */
export const PEPPOL_SCHEME_BE_BUSINESS_NUMBER = '0208'

/**
 * `unknown` means "no rule applies here", not "wrong": a foreign identifier or
 * an empty field shows no verdict at all rather than a red cross.
 */
export type IdentifierStatus = 'valid' | 'invalid' | 'unknown'

/** Digits only: users type dots, spaces and dashes, and the API stores them. */
function digitsOf(value: string | null | undefined): string {
  return (value ?? '').replace(/\D/g, '')
}

/**
 * Whether the value carries a Peppol scheme in front of the number, as in
 * `0208:0123456749`. Only a scheme the back actually emits counts, so a GLN
 * that legitimately starts with those four digits is not flagged: the prefix
 * has to be followed by a separator.
 */
export function hasSchemePrefix(value: string | null | undefined): boolean {
  return new RegExp(
    `^\\s*(${PEPPOL_SCHEME_GLN}|${PEPPOL_SCHEME_BE_BUSINESS_NUMBER})\\s*[:\\-]`
  ).test(value ?? '')
}

/**
 * Belgian enterprise number: 10 digits opening on 0 or 1, the last two being
 * 97 minus the first eight modulo 97.
 */
export function checkBelgianBusinessNumber(
  value: string | null | undefined
): IdentifierStatus {
  if (hasSchemePrefix(value)) {
    return 'invalid'
  }

  const digits = digitsOf(value)

  if (digits === '') {
    return 'unknown'
  }

  if (!/^[01]\d{9}$/.test(digits)) {
    return 'invalid'
  }

  const base = Number(digits.slice(0, 8))
  const control = Number(digits.slice(8, 10))

  return 97 - (base % 97) === control ? 'valid' : 'invalid'
}

/**
 * GLN: the 13-digit GS1 code, whose last digit is the EAN-13 check digit —
 * the first twelve weighted 1 and 3 alternately, completed to the next ten.
 */
export function checkGln(value: string | null | undefined): IdentifierStatus {
  if (hasSchemePrefix(value)) {
    return 'invalid'
  }

  const digits = digitsOf(value)

  if (digits === '') {
    return 'unknown'
  }

  if (!/^\d{13}$/.test(digits)) {
    return 'invalid'
  }

  const sum = digits
    .slice(0, 12)
    .split('')
    .reduce(
      (total, digit, index) =>
        total + Number(digit) * (index % 2 === 0 ? 1 : 3),
      0
    )

  return (10 - (sum % 10)) % 10 === Number(digits[12]) ? 'valid' : 'invalid'
}

/**
 * VAT number format per country, the two-letter code included, as VIES expects
 * it. Moved from BaseShowEditInputVAT, which still sends the back a lookup once
 * the format matches.
 */
export const VAT_PATTERNS: Record<string, RegExp> = {
  AT: /^ATU[A-Z\d]{8}$/,
  BE: /^BE(0\d{9}|\d{10})$/,
  BG: /^BG\d{9,10}$/,
  CH: /^CH(\d{6}|[E]\d{9}\s?(TVA|MWST|IVA))$/,
  CY: /^CY\d{8}[A-Z]$/,
  CZ: /^CZ\d{8,10}$/,
  DE: /^DE\d{9}$/,
  DK: /^DK(\d{2} ?){3}\d{2}$/,
  EE: /^EE\d{9}$/,
  EL: /^EL\d{9}$/,
  ES: /^ES([A-Z]\d{7}[A-Z]|\d{8}[A-Z]|[A-Z]\d{8})$/,
  FI: /^FI\d{8}$/,
  FR: /^FR([A-Z]{2}|\d{2})\d{9}$/,
  GB: /^GB(\d{9}|\d{12}|(GD|HA)\d{3})$/,
  HR: /^HR\d{11}$/,
  HU: /^HU\d{8}$/,
  IE: /^IE([A-Z\d]{8}|[A-Z\d]{9})$/,
  IT: /^IT\d{11}$/,
  LT: /^LT(\d{9}|\d{12})$/,
  LU: /^LU\d{8}$/,
  LV: /^LV\d{11}$/,
  MT: /^MT\d{8}$/,
  NL: /^NL\d{9}B\d{2}$/,
  PL: /^PL\d{10}$/,
  PT: /^PT\d{9}$/,
  RO: /^RO\d{2,10}$/,
  SE: /^SE\d{12}$/,
  SI: /^SI\d{8}$/,
  SK: /^SK\d{10}$/
}

/** Letters and digits only, upper-cased: `be 0123.456.749` → `BE0123456749`. */
export function normalizeVat(value: string | null | undefined): string {
  return (value ?? '').replace(/[^A-Z0-9]/gi, '').toUpperCase()
}

/**
 * VAT number format. The two-letter country code is required: a number typed
 * without it (`0123456749`), or behind a Peppol scheme (`9925:BE…`), is
 * invalid. A country with no known pattern gets no verdict.
 */
export function checkVatFormat(
  value: string | null | undefined
): IdentifierStatus {
  const normalized = normalizeVat(value)

  if (normalized === '') {
    return 'unknown'
  }

  if (!/^[A-Z]{2}/.test(normalized)) {
    return 'invalid'
  }

  const pattern = VAT_PATTERNS[normalized.slice(0, 2)]

  if (!pattern) {
    return 'unknown'
  }

  return pattern.test(normalized) ? 'valid' : 'invalid'
}
